import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Typography, 
    Button, 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    Switch,
    Slide, 
    Link,
    IconButton,
    useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { updateAnalyticsConsent } from '../shared/analytics/ga4';

const CookieConsent = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [preferences, setPreferences] = useState({
        essential: true,
        analytics: true,
        marketing: true
    });
    const theme = useTheme();

    const cookieButtonSx = {
        borderRadius: '14px',
        fontFamily: '"JetBrains Mono", monospace',
        letterSpacing: '0em',
        textTransform: 'none',
        '&:hover': {
            transform: 'none', // Portal buttons typically don't "float" on hover.
        },
    };

	    const dialogPaperSx = {
	        borderRadius: '14px',
	        border: 1,
	        borderColor: 'divider',
	        boxShadow: theme.shadows[12],
	        overflow: 'hidden',
	        // Keep the dialog inside the viewport and let DialogContent scroll if needed.
	        maxHeight: 'calc(100dvh - 48px)',
	        display: 'flex',
	        flexDirection: 'column',
	    };

	    const cookieSwitchSx = {
	        // Enabled "on" state: monochrome (no theme primary red).
	        '& .MuiSwitch-switchBase.Mui-checked': { color: 'common.black' },
	        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'grey.800' },
	        // Disabled state (including checked): keep it gray.
	        '& .MuiSwitch-switchBase.Mui-disabled': { color: 'grey.400' },
	        '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': { backgroundColor: 'grey.300' },
	        '& .MuiSwitch-switchBase.Mui-disabled.Mui-checked': { color: 'grey.500' },
	        '& .MuiSwitch-switchBase.Mui-disabled.Mui-checked + .MuiSwitch-track': { backgroundColor: 'grey.400' },
	    };

    const applyConsent = (prefs) => {
        updateAnalyticsConsent(prefs);
    };

    // Helper to manage cookies
    const setCookie = (name, value, days) => {
        let expires = "";
        if (days) {
            const date = new Date();
            date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
            expires = "; expires=" + date.toUTCString();
        }
        document.cookie = name + "=" + (value || "") + expires + "; path=/; SameSite=Lax";
    };

    const getCookie = (name) => {
        const nameEQ = name + "=";
        const ca = document.cookie.split(';');
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i];
            while (c.charAt(0) === ' ') c = c.substring(1, c.length);
            if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
        }
        return null;
    };

    useEffect(() => {
        const consent = getCookie('r5_cookie_consent');
        if (!consent) {
            setIsVisible(true);
        } else {
            try {
                const savedPrefs = JSON.parse(consent);
                // Guard against legacy/invalid cookie formats.
                if (!savedPrefs || typeof savedPrefs !== 'object') throw new Error('invalid consent cookie');
                setPreferences(savedPrefs);
                applyConsent(savedPrefs);
            } catch (e) {
                // If parsing fails, reset consent so the banner can be shown again.
                setCookie('r5_cookie_consent', '', -1);
                setIsVisible(true);
            }
        }

        const openSettings = () => setShowModal(true);
        window.addEventListener('openCookieSettings', openSettings);

        return () => {
            window.removeEventListener('openCookieSettings', openSettings);
        };
    }, []);

    const handleAcceptAll = () => {
        const allEnabled = { essential: true, analytics: true, marketing: true };
        setCookie('r5_cookie_consent', JSON.stringify(allEnabled), 365);
        applyConsent(allEnabled);
        setIsVisible(false);
        setShowModal(false);
    };

    const handleSavePreferences = () => {
        setCookie('r5_cookie_consent', JSON.stringify(preferences), 365);
        applyConsent(preferences);
        setIsVisible(false);
        setShowModal(false);
    };

    const togglePreference = (key) => {
        if (key === 'essential') return;
        setPreferences(prev => ({ ...prev, [key]: !prev[key] }));
    };

    if (!isVisible && !showModal) return null;

    return (
        <>
            <Slide direction="up" in={isVisible && !showModal} mountOnEnter unmountOnExit>
                <Box 
                    component="section"
                    data-ui="cookie-consent-bar"
                    sx={{
                        position: 'fixed',
                        bottom: { xs: 'calc(64px + env(safe-area-inset-bottom, 0px))', md: 0 },
                        left: 0,
                        width: '100%',
                        // Transparent shell to allow the inner card to match portal styling.
                        backgroundColor: 'transparent',
                        zIndex: 22010,
                        padding: { xs: 2, md: 3 },
                        pointerEvents: 'none',
                    }}
                >
                    <Box 
                        sx={{ 
                            maxWidth: '1200px',
                            margin: '0 auto',
                            px: { xs: 3.5, sm: 4, md: 5 },
                            py: { xs: 3, md: 3.5 },
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            justifyContent: 'space-between',
                            alignItems: { xs: 'flex-start', md: 'center' },
                            gap: { xs: 2.5, md: 5 },
                            backgroundColor: 'background.paper',
                            border: 1,
                            borderColor: 'divider',
                            borderTop: '3px solid #000',
                            boxShadow: theme.shadows[6],
                            borderRadius: '14px',
                            maxHeight: { xs: 'calc(100dvh - 96px - env(safe-area-inset-top, 0px))', md: 'none' },
                            overflowY: { xs: 'auto', md: 'visible' },
                            pointerEvents: 'auto',
                            boxSizing: 'border-box',
                        }}
                    >
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="h3" sx={{ m: 0, mb: 1.5, fontSize: '1.25rem', color: 'text.primary', fontWeight: 700 }}>
                                Sua privacidade é importante para nós
                            </Typography>
                            <Typography sx={{ m: 0, fontSize: '0.9rem', color: 'text.secondary', lineHeight: 1.6 }}>
                                Nós usamos cookies e tecnologias semelhantes para ajudar a identificar você e seus dispositivos, operar nosso site,
                                melhorar sua experiência e realizar publicidade e análises. Alguns desses cookies são opcionais e são usados apenas
                                quando você concorda com eles. Você pode consentir com todos os nossos cookies opcionais de uma só vez ou gerenciar
                                suas próprias preferências através do link "Gerenciar Escolhas". Você pode ler mais sobre esses usos em nossa{' '}
	                                <Link 
	                                    href="/governanca/privacy_policy" 
	                                    data-ui="link-privacy-policy"
	                                    sx={{
	                                        color: 'text.primary',
	                                        fontWeight: 600,
	                                        textDecoration: 'underline',
	                                        textDecorationColor: 'grey.400',
	                                        textUnderlineOffset: '3px',
	                                        '&:hover': { textDecorationColor: 'text.primary' }
	                                    }}
	                                >
	                                    Declaração de Privacidade
	                                </Link>.
                            </Typography>
                        </Box>
                        <Box 
                            sx={{ 
                                display: 'flex', 
                                gap: 2, 
                                alignItems: 'center', 
                                flexShrink: 0, 
                                width: { xs: '100%', md: 'auto' }, 
                                flexDirection: { xs: 'column', sm: 'row' } 
                            }}
                        >
                            <Button 
                                onClick={() => setShowModal(true)}
                                data-ui="btn-manage-cookies"
                                variant="outlined"
                                color="secondary"
                                sx={{ 
                                    ...cookieButtonSx,
                                    fontWeight: 700,
                                    fontSize: '0.9rem',
                                    width: { xs: '100%', sm: 'auto' },
                                    borderWidth: '1px',
                                    '&:hover': {
                                        ...cookieButtonSx['&:hover'],
                                        backgroundColor: 'action.hover',
                                        borderWidth: '1px',
                                    },
                                }}
                            >
                                Gerenciar Escolhas
                            </Button>
	                            <Button 
	                                onClick={handleAcceptAll}
	                                variant="contained"
	                                color="secondary"
	                                data-ui="btn-accept-all-cookies"
	                                sx={{ 
	                                    ...cookieButtonSx,
	                                    backgroundColor: 'common.black',
	                                    color: 'common.white',
	                                    px: 3, 
	                                    py: 1.5, 
	                                    fontWeight: 700, 
	                                    fontSize: '0.95rem',
	                                    width: { xs: '100%', sm: 'auto' },
	                                    '&:hover': {
	                                        ...cookieButtonSx['&:hover'],
	                                        backgroundColor: 'grey.900',
	                                    },
	                                }}
	                            >
	                                Aceitar Todos os Cookies
	                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Slide>

	            <Dialog 
	                open={showModal} 
	                onClose={() => setShowModal(false)}
	                data-ui="cookie-settings-modal"
	                maxWidth="sm"
	                fullWidth
	                scroll="paper"
	                sx={{
	                    '& .MuiDialog-container': {
	                        alignItems: 'center',
	                    },
	                }}
	                // MUI v7 prefers slotProps; keep PaperProps for compatibility.
	                slotProps={{
	                    paper: {
	                        sx: {
	                            ...dialogPaperSx,
	                            m: { xs: 2, sm: 3 },
	                            width: { xs: 'calc(100vw - 32px)', sm: 'min(920px, calc(100vw - 48px))' },
	                        }
	                    },
	                    backdrop: { sx: { backgroundColor: 'rgba(0,0,0,0.55)' } },
	                }}
	                PaperProps={{
	                    sx: {
	                        ...dialogPaperSx,
	                        m: { xs: 2, sm: 3 },
	                        width: { xs: 'calc(100vw - 32px)', sm: 'min(920px, calc(100vw - 48px))' },
	                    }
	                }}
	            >
	                <DialogTitle
	                    sx={{
	                        m: 0,
	                        p: 2.5,
	                        borderBottom: 1,
	                        borderColor: 'divider',
	                        borderTop: '3px solid #000',
	                        display: 'flex',
	                        justifyContent: 'space-between',
	                        alignItems: 'center',
	                        backgroundColor: 'background.paper',
	                    }}
	                >
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 800,
                            fontFamily: '"JetBrains Mono", monospace',
                            letterSpacing: '0.04em',
                            textTransform: 'uppercase',
                            fontSize: '1.05rem',
                        }}
                    >
                        Gerenciar Preferências de Cookies
                    </Typography>
                    <IconButton 
                        onClick={() => setShowModal(false)} 
                        size="small" 
                        data-ui="btn-close-modal"
                        sx={{
                            color: 'text.secondary',
                            borderRadius: '14px',
                            border: 1,
                            borderColor: 'divider',
                            '&:hover': { backgroundColor: 'action.hover' },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ px: { xs: 2.5, sm: 3.5 }, py: 3, flex: '1 1 auto', overflowY: 'auto' }}>
                    <Typography sx={{ mb: 3, fontSize: '0.95rem', color: 'text.secondary' }}>
                        Quando você visita qualquer site, ele pode armazenar ou recuperar informações no seu navegador, principalmente na forma de cookies. Aqui você pode gerenciar suas preferências.
                    </Typography>

	                    <Box sx={{ borderBottom: 1, borderColor: 'divider', py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
	                        <Box sx={{ pr: 2 }}>
	                            <Typography sx={{ fontWeight: 700, color: 'text.primary' }}>Estritamente Necessários</Typography>
	                            <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>Estes cookies são necessários para o funcionamento do site e não podem ser desativados.</Typography>
	                        </Box>
		                        <Switch checked={true} disabled sx={cookieSwitchSx} />
	                    </Box>

                    <Box sx={{ borderBottom: 1, borderColor: 'divider', py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ pr: 2 }}>
                            <Typography sx={{ fontWeight: 700, color: 'text.primary' }}>Análise e Desempenho</Typography>
                            <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>Nos permitem contar visitas e fontes de tráfego para que possamos medir e melhorar o desempenho do nosso site.</Typography>
                        </Box>
		                        <Switch 
		                            checked={preferences.analytics} 
		                            data-ui="switch-analytics"
		                            onChange={() => togglePreference('analytics')}
		                            sx={cookieSwitchSx}
		                        />
	                    </Box>

                    <Box sx={{ borderBottom: 1, borderColor: 'divider', py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ pr: 2 }}>
                            <Typography sx={{ fontWeight: 700, color: 'text.primary' }}>Marketing e Publicidade</Typography>
                            <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>Podem ser definidos pelos nossos parceiros de publicidade para criar um perfil dos seus interesses.</Typography>
                        </Box>
		                        <Switch 
		                            checked={preferences.marketing} 
		                            data-ui="switch-marketing"
		                            onChange={() => togglePreference('marketing')}
		                            sx={cookieSwitchSx}
		                        />
	                    </Box>
                </DialogContent>
	                <DialogActions
	                    sx={{
	                        p: 2.5,
	                        borderTop: 1,
	                        borderColor: 'divider',
	                        gap: 1.5,
	                        display: 'grid',
	                        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
	                        alignItems: 'stretch',
	                        backgroundColor: 'background.paper',
	                    }}
	                >
	                    <Button 
	                        onClick={() => setShowModal(false)} 
	                        variant="outlined" 
	                        color="secondary"
	                        data-ui="btn-cancel-settings"
	                        sx={{
	                            ...cookieButtonSx,
	                            width: '100%',
	                            minWidth: 0,
	                            height: 52,
	                            borderWidth: '1px',
	                            px: 3,
	                            py: 1.25,
	                            whiteSpace: 'nowrap',
	                            '&:hover': {
	                                ...cookieButtonSx['&:hover'],
	                                backgroundColor: 'action.hover',
	                                borderWidth: '1px',
	                            }
	                        }}
	                    >
	                        Voltar
	                    </Button>
	                    <Button 
	                        onClick={handleSavePreferences} 
	                        variant="contained" 
	                        color="secondary"
	                        data-ui="btn-save-settings"
	                        sx={{
	                            ...cookieButtonSx,
	                            backgroundColor: 'common.black',
	                            color: 'common.white',
	                            width: '100%',
	                            minWidth: 0,
	                            height: 52,
	                            px: 3,
	                            py: 1.25,
	                            whiteSpace: 'nowrap',
	                            '&:hover': {
	                                ...cookieButtonSx['&:hover'],
	                                backgroundColor: 'grey.900',
	                            }
	                        }}
	                    >
	                        Salvar
	                    </Button>
	                    <Button 
	                        onClick={handleAcceptAll} 
	                        variant="contained" 
	                        color="secondary"
	                        data-ui="btn-accept-all-modal"
	                        sx={{
	                            ...cookieButtonSx,
	                            backgroundColor: 'grey.800',
	                            color: 'common.white',
	                            width: '100%',
	                            minWidth: 0,
	                            height: 52,
	                            px: 3,
	                            py: 1.25,
	                            whiteSpace: 'nowrap',
	                            '&:hover': {
	                                ...cookieButtonSx['&:hover'],
	                                backgroundColor: 'grey.900',
	                            }
	                        }}
	                    >
	                        Aceitar Todos
	                    </Button>
	                </DialogActions>
            </Dialog>
        </>
    );
};

export default CookieConsent;
